import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  createBlogCategory,
  getAllBlogCategoriesAdmin,
  deleteBlogCategory,
} from "../../services/api";

export default function AddBlogCategory() {
  const [categoryName, setCategoryName] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await getAllBlogCategoriesAdmin({ limit: 100 });
      setCategories(res?.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch blog categories", err);
      setSnack({
        open: true,
        severity: "error",
        message: "Failed to load categories",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddCategory = async () => {
    const name = categoryName.trim();
    if (!name) {
      setSnack({
        open: true,
        severity: "error",
        message: "Please enter a category name",
      });
      return;
    }

    setLoading(true);
    try {
      await createBlogCategory({ name });
      setSnack({
        open: true,
        severity: "success",
        message: "Category added successfully",
      });
      setCategoryName("");
      await loadCategories();
    } catch (err) {
      console.error("createBlogCategory err", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to add category";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
  // dynamic import ensures Swal is available at runtime
  let Swal;
  try {
    Swal = (await import("sweetalert2")).default;
  } catch (impErr) {
    console.error("Failed to load sweetalert2", impErr);
    setSnack({ open: true, severity: "error", message: "Failed to load confirmation dialog" });
    return;
  }

  const result = await Swal.fire({
    title: "Are you sure?",
    text: "This category will be permanently deleted.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, delete it",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    reverseButtons: true,
    focusCancel: true,
  });

  if (!result.isConfirmed) return;

  // show loading modal while deleting
  Swal.fire({
    title: "Deleting...",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  setLoading(true);
  try {
    await deleteBlogCategory(id);

    Swal.close();
    await Swal.fire({
      title: "Deleted!",
      text: "Category has been deleted.",
      icon: "success",
      timer: 1400,
      showConfirmButton: false,
    });

    setSnack({ open: true, severity: "success", message: "Category deleted" });
    await loadCategories();
  } catch (err) {
    console.error("delete blog category err", err);
    Swal.close();
    await Swal.fire({
      title: "Error",
      text: err?.response?.data?.message || "Failed to delete category.",
      icon: "error",
      confirmButtonColor: "#3085d6",
    });
    setSnack({ open: true, severity: "error", message: "Failed to delete category" });
  } finally {
    setLoading(false);
  }
};


  return (
    <Box sx={{ pt: { xs: 8, md: 10 }, pb: 6 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
          <Typography variant="h6" mb={2}>
            Add Blog Category
          </Typography>

          <Box display="flex" gap={2} mb={2}>
            <TextField
              label="Category Name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              fullWidth
              disabled={loading}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddCategory}
              disabled={loading}
            >
              Add
            </Button>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <Typography variant="subtitle1" mb={1}>
            Existing Blog Categories
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Category Name</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No categories found
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell>{cat.id}</TableCell>
                      <TableCell>{cat.name}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(cat.id)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.severity}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
